import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { SiteNav } from "@/components/media/SiteNav";
import { Eyebrow } from "@/components/media/Sections";
import { CheckCircle2, XCircle } from "lucide-react";

/** Where Stripe sends a catering customer after the deposit. */
export default function DepositSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const site = useSite();
  const [state, setState] = useState({ status: "checking", amount: 0 });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", amount: 0 });
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/deposits/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", amount: data.amount });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState({ status: "error", amount: 0 });
          return;
        }
      } catch {
        setState({ status: "error", amount: 0 });
        return;
      }
      if (tries >= 10) {
        setState({ status: "pending", amount: 0 });
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="deposit-success-page">
      <SiteNav />
      <div className="mx-auto max-w-lg px-5 py-24 text-center">
        {state.status === "checking" && (
          <>
            <Eyebrow>CONFIRMING WITH STRIPE</Eyebrow>
            <h1 className="neon mt-4 text-3xl">Checking your deposit…</h1>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-primary" size={30} />
            <h1 className="neon mt-6 text-4xl">Your date is locked in.</h1>
            <p data-testid="deposit-paid" className="mt-4 text-base text-muted-foreground">
              We received your ${state.amount.toLocaleString("en-US")} deposit. A receipt is on its
              way to your inbox, and it applies to your final balance.
            </p>
            {site?.catering?.deposit_policy ? (
              <p className="mono mt-6 text-[10px] leading-relaxed tracking-widest text-muted-foreground">
                {site.catering.deposit_policy}
              </p>
            ) : null}
            <Link
              to="/events#menu"
              data-testid="deposit-success-cta"
              className="display mt-8 inline-flex rounded-sm bg-primary px-7 py-4 text-xl uppercase text-primary-foreground transition-colors hover:bg-[#E6C800]"
            >
              Pick your flavors
            </Link>
          </>
        )}
        {state.status === "pending" && (
          <>
            <h1 className="neon mt-4 text-3xl">Still processing</h1>
            <p data-testid="deposit-pending" className="mt-4 text-base text-muted-foreground">
              Stripe hasn't confirmed yet. Refresh in a minute — nothing is lost.
            </p>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="mx-auto text-destructive" size={28} />
            <h1 className="neon mt-6 text-3xl">We couldn't confirm that</h1>
            <p data-testid="deposit-error" className="mt-4 text-base text-muted-foreground">
              Your date isn't held yet. If you were charged, reply to our email and we'll sort it
              out straight away.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
